import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { env } from '../env';
import { BrandLogo } from '@nexdel/ui/layout/BrandLogo';
import { acceptInviteSchema, type AcceptInviteForm } from '../schemas/accept-invite';

export default function AcceptInvite() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [serverError, setServerError] = useState<string | null>(null);
  const [wrongApp, setWrongApp] = useState(false);
  const [done, setDone] = useState(false);

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const toggleLang = () => i18n.changeLanguage(currentLang === 'fr' ? 'en' : 'fr');

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<AcceptInviteForm>({
    resolver: zodResolver(acceptInviteSchema),
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-[#081208] flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Invalid invitation link</h2>
          <p className="text-white/40 text-sm">The link is missing a token. Please use the link from your invitation email.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#081208] flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {currentLang === 'fr' ? 'Compte créé' : 'Account created'}
          </h2>
          <p className="text-white/40 text-sm mb-6">
            {currentLang === 'fr'
              ? 'Votre compte a été créé. Redirection en cours…'
              : 'Your account has been created. Redirecting…'}
          </p>
        </div>
      </div>
    );
  }

  if (wrongApp) {
    return (
      <div className="min-h-screen bg-[#081208] flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {currentLang === 'fr' ? 'Mauvaise application' : 'Wrong app'}
          </h2>
          <p className="text-white/40 text-sm">
            {currentLang === 'fr'
              ? 'Cette invitation est destinée à l\'application cliente. Contactez votre administrateur.'
              : 'This invitation is for the client app. Please contact your administrator.'}
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: AcceptInviteForm) => {
    setServerError(null);

    try {
      const res = await fetch(`${env.VITE_API_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: data.password,
        }),
      });

      const body = await res.json();

      if (!res.ok) {
        setServerError(body.message ?? 'Something went wrong. Please try again.');
        return;
      }

      if (body.userKind === 'client_user') {
        setWrongApp(true);
        return;
      }

      setDone(true);

      // Sign in with the email/password the user just set — this yields a "password" first-factor
      // session (unlike a custom token), which is what Firebase requires to enroll MFA afterwards.
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, body.email, data.password);
      navigate('/dashboard', { replace: true });
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#081208] font-sans antialiased flex text-white/90">
      {/* Left two-thirds — atmospheric branding */}
      <div className="hidden md:flex flex-col flex-[2] relative overflow-hidden">
        <iframe
          src="https://my.spline.design/trails-e13WCmJCVeHdNBlOEyk0xzug/"
          frameBorder={0}
          className="absolute inset-0 w-full h-full blur-sm"
        />

        <div className="relative z-10 flex flex-col justify-center items-center h-full p-12 text-center gap-8">
          {/* Logo */}
          <BrandLogo className="h-24 w-auto" />

          {/* Tagline */}
          <div className="max-w-lg">
            <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-tight mb-6 whitespace-pre-line">
              {t('login.supplierHeroTitle')}
            </h1>
            <p className="text-white/60 text-lg leading-relaxed mb-8">
              {t('login.supplierHeroSubtitle')}
            </p>
            <ul className="space-y-3">
              {(['supplierFeature1', 'supplierFeature2', 'supplierFeature3'] as const).map((key) => (
                <li key={key} className="flex items-center justify-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-white/50 text-base leading-relaxed">{t(`login.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-12 relative">
        <button
          type="button"
          onClick={toggleLang}
          className="absolute top-6 right-6 text-xs text-white/40 hover:text-white/70 transition-colors uppercase tracking-widest"
        >
          {currentLang === 'fr' ? 'EN' : 'FR'}
        </button>

        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-2xl font-semibold text-white mb-1">
            {currentLang === 'fr' ? 'Créer votre compte' : 'Create your account'}
          </h2>
          <p className="text-white/40 text-sm mb-8">
            {currentLang === 'fr'
              ? 'Choisissez un mot de passe pour accéder au portail.'
              : 'Choose a password to access the portal.'}
          </p>

          {serverError && (
            <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                {currentLang === 'fr' ? 'Mot de passe' : 'Password'}
              </label>
              <input
                {...register('password')}
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                {currentLang === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </label>
              <input
                {...register('confirmPassword')}
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors mt-2"
            >
              {isSubmitting
                ? (currentLang === 'fr' ? 'Création…' : 'Creating account…')
                : (currentLang === 'fr' ? 'Créer mon compte' : 'Create account')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
